import React, { useState, Fragment, useMemo, useCallback } from 'react';
import { find, isEmpty, propEq } from 'ramda';
import Collapse from '@material-ui/core/Collapse/Collapse';

import { useIntl } from 'gatsby-plugin-intl';
import { navigate } from 'gatsby';

import { ReactComponent as AboutIcon } from '../../images/about-icon.svg';
import { ReactComponent as DownloadIcon } from '../../images/download-icon.svg';
import { ReactComponent as DownloadReportIcon } from '../../images/download-report-icon.svg';
import { ReactComponent as ShareIcon } from '../../images/share-icon.svg';
import { ReactComponent as CloseIcon } from '../../images/close-icon.svg';
import { isDocumentAvailable } from '../../utils/browserObjectsAvailability';
import { REPORT_CONTENT_ID, DATA_PATH, REPORT_PATH } from '../../utils/constants';
import { useWindowLocation } from '../../hooks/useWindowLocation';
import { useShareModal } from '../../hooks/useShareModal';
import ShareModal from '../shareModal/shareModal.component';
import tocData from './tableOfContents.data.json';
import {
  TableOfContentsWrapper,
  Drawer,
  DrawerContent,
  OpenDrawerContent,
  CollapsedDrawerContent,
  CloseButton,
  Logo,
  Title,
  SectionList,
  Section,
  SectionTitle,
  SubSection,
  SubSectionTitleWrapper,
  SubSectionTitle,
  Counter,
  CounterLink,
  PagesList,
  PageItem,
  PageLink,
  HiddenLink,
  DotSvg,
  DotCircle,
  Page,
  ActionsList,
  ActionItem,
  ActionLabel,
} from './tableOfContents.styles';
import {
  DOT_SIZE,
  DOT_POSITION,
  DOT_RADIUS,
  INTRODUCTION,
  ANIMATION_DURATION,
  CLOSE_ICON,
} from './tableOfContents.constants';
import { Modal } from '../modal';
import { AboutModal } from '../aboutModal/aboutModal.component';
import { MobileMenuButton } from './mobileMenuButton';
import { useBreakpointsContext } from '../../store/BreakpointsContext';
import { closeOverlay, openOverlay } from '../../utils/overlay';
import { ScrollToTopButton } from '../scrollToTopButton';

export const TableOfContents = ({ tableOfContents, logoList = [], activeMenu, hash }) => {
  const { isDesktop, isMobile, isTabletOrGreater, isSet } = useBreakpointsContext();
  const [activePage, setActivePage] = useState(hash.replace('#', ''));
  const [open, setOpen] = useState(isDesktop);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const aboutModalStyles = (isMobile || isTabletOrGreater) && !isDesktop ? { content: { height: '65%' } } : {};

  const handleDrawerOpen = () => {
    if (open) return;

    setOpen(true);
    if (!isDesktop) {
      setTimeout(() => {
        openOverlay(isMobile);
      }, ANIMATION_DURATION);
    }
  };

  const handleDrawerClose = () => {
    if (!open) return;

    setOpen(false);
    if (!isDesktop) {
      closeOverlay(isMobile);
    }
  };

  const handleSetActive = (displayName) => () => {
    setActivePage(displayName);
  };

  const handleClick = () => {
    if (!isDesktop) {
      setOpen(false);
      closeOverlay();
    }
  };

  const handleModalOpen = () => {
    if (open && !isDesktop) {
      handleDrawerClose();
    }
    setAboutModalOpen(true);
  };

  const { title, structure } = tocData;
  const intl = useIntl();

  const isActive = useCallback((slug) => activeMenu.indexOf(slug) > 0, [activeMenu]);

  const currentSection = useMemo(() => {
    const mappedData = structure.reduce((acc, structure, structureIndex) => {
      const childItems = structure.items.map((item) => ({
        index: structureIndex,
        isActive: isActive(item.id),
        number: item.index,
        isVisible: item.isMobileVisible
      }));

      return [...acc, ...childItems];
    }, []);

    return mappedData.find(propEq('isActive', true));
  }, [structure, isActive]);

  const Dot = () => (
    <DotSvg viewBox={`0 0 ${DOT_SIZE} ${DOT_SIZE}`} height={DOT_SIZE} width={DOT_SIZE}>
      <DotCircle r={DOT_RADIUS} cx={DOT_POSITION} cy={DOT_POSITION} shape-rendering="geometricPrecision" />
    </DotSvg>
  );

  const renderPage = (selected, activePage, sectionIndex) => ({ name, displayName }, key) => (
    <PageItem key={key} selected={activePage === displayName} index={sectionIndex}>
      <PageLink
        to={displayName}
        containerId={REPORT_CONTENT_ID}
        spy={true}
        onSetActive={handleSetActive(displayName)}
        hashSpy={true}
        onClick={handleClick}
      >
        <Dot />
        <Page>{intl.formatMessage({ id: name.replace(/^\d+\.\d+ /, '')})}</Page>
      </PageLink>
    </PageItem>
  );

  const scrollToTop = () => {
    setActivePage(null);
    isDocumentAvailable() && document.querySelector(`#${REPORT_CONTENT_ID}`).scrollTo(0, 0);
  };

  const handleScrollToTopClick = () => {
    setTimeout(() => {
      scrollToTop();
    }, 500);
  };

  const renderSubSection = ({ data, isActive, activePage }, sectionIndex) => ({ index, id, showPages }, key) => {
    const { name, slug, pages } = find(propEq('slug', id))(data) || {};
    const selected = isActive(slug);
    const intl = useIntl();
    return (
      <SubSection key={key} selected={selected} index={sectionIndex} disablePadding>
        <SubSectionTitleWrapper to={`/report/${slug}`} onClick={scrollToTop}>
          <Counter>{index}</Counter>
          <SubSectionTitle>{intl.formatMessage({ id: name })}</SubSectionTitle>
        </SubSectionTitleWrapper>
        {showPages && (
          <Collapse in={selected} timeout="auto">
            <PagesList>
              <HiddenLink
                to={INTRODUCTION}
                containerId={REPORT_CONTENT_ID}
                spy={true}
                onSetActive={handleSetActive(null)}
              />
              {pages.filter(({ slug }) => slug !== INTRODUCTION).map(renderPage(selected, activePage, sectionIndex))}
            </PagesList>
          </Collapse>
        )}
      </SubSection>
    );
  };

  const renderSection = (props) => ({ title, items, id, isVisible }, groupIndex) => {
    const intl = useIntl();
    if(!isVisible) return null
    return (
      <Section key={id}>
        <SectionTitle index={groupIndex}>{intl.formatMessage({ id: title })}</SectionTitle>
        {items.map(renderSubSection(props, groupIndex))}
      </Section>
    );
  };

  const renderShortcut = ({ isActive }) => ({ items }, groupIndex) => {
    return items.map(({ id: slug, index, isMobileVisible }, key) => {
      if(!isMobileVisible) return null
      else return (
        <CounterLink to={`/report/${slug}`} selected={isActive(slug)} index={groupIndex} key={key} tabIndex={-1}>
          <Counter>{intl.formatMessage({ id: index })}</Counter>
        </CounterLink>
      )
    });
  };

  const handleDownloadData = (path) => () => {
    window.location.href = path;
  };

  const onTitleClick = (e) => {
    e.preventDefault();

    handleDrawerClose();
    navigate('/');
  };

  const shareModalState = useShareModal();
  const windowLocation = useWindowLocation()() || {};
  const getLinkToShare = () =>
    [
      windowLocation.origin,
      `${windowLocation.pathname.replace(/\/$/, '')}/`,
      isEmpty(windowLocation.hash) ? '' : `${windowLocation.hash.replace('#', '')}/`,
    ].join('');

  return (
    <TableOfContentsWrapper>
      {isSet && (
        <Fragment>
          <Drawer open={open}>
            <DrawerContent>
              <CloseButton onClick={handleDrawerClose}>
                <CloseIcon width={CLOSE_ICON.size.width} height={CLOSE_ICON.size.height} />
              </CloseButton>
              <Logo onClick={handleDrawerOpen} />
              <OpenDrawerContent>
                <Title onClick={onTitleClick} onKeyDown={onTitleClick}>
                  {intl.formatMessage({ id: title })}
                </Title>
                <SectionList>
                  {structure.map(
                    renderSection({
                      data: tableOfContents,
                      activePage,
                      isActive,
                    })
                  )}
                </SectionList>
              </OpenDrawerContent>
              <CollapsedDrawerContent>
                {structure.map(
                  renderShortcut({
                    isActive,
                  })
                )}
              </CollapsedDrawerContent>
              <ActionsList>
                <ActionItem onClick={handleModalOpen}>
                  <AboutIcon />
                  <ActionLabel>{intl.formatMessage({ id: 'About' })}</ActionLabel>
                </ActionItem>
                <ActionItem onClick={handleDownloadData(DATA_PATH)}>
                  <DownloadIcon />
                  <ActionLabel>{intl.formatMessage({ id: 'Download Data' })}</ActionLabel>
                </ActionItem>
                <ActionItem onClick={shareModalState.onRequestOpen}>
                  <ShareIcon width={20} height={20} />
                  <ActionLabel>{intl.formatMessage({ id: 'Share' })}</ActionLabel>
                </ActionItem>
              </ActionsList>
            </DrawerContent>
          </Drawer>
          {shareModalState.isOpen && <ShareModal modalProps={shareModalState} linkToShare={getLinkToShare()} />}
          <Modal isOpen={aboutModalOpen} onRequestClose={() => setAboutModalOpen(false)} styles={aboutModalStyles}>
            <AboutModal logoList={logoList} />
          </Modal>
          {!isDesktop && <MobileMenuButton section={currentSection} onClick={handleDrawerOpen} />}
          {!isDesktop && <ScrollToTopButton onClick={handleScrollToTopClick} />}
        </Fragment>
      )}
    </TableOfContentsWrapper>
  );
};
